import { useState, useEffect } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { 
  FileText, 
  TrendingUp, 
  Upload, 
  CheckCircle, 
  Gift, 
  User, 
  Search, 
  Bookmark, 
  Heart, 
  Calculator, 
  MessageCircle, 
  LogIn, 
  Plane,
  Activity,
  ChevronRight
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { formatDistanceToNow } from 'date-fns';

const API_URL = window.location.origin;

// Icon mapping
const ICONS = {
  FileText, TrendingUp, Upload, CheckCircle, Gift, User, Search, 
  Bookmark, Heart, Calculator, MessageCircle, LogIn, Plane, Activity
};

// Color mapping
const COLORS = {
  blue: 'bg-blue-100 text-blue-600',
  cyan: 'bg-cyan-100 text-cyan-600',
  emerald: 'bg-emerald-100 text-emerald-600',
  green: 'bg-green-100 text-green-600',
  violet: 'bg-violet-100 text-violet-600',
  amber: 'bg-amber-100 text-amber-600',
  slate: 'bg-slate-100 text-slate-600',
  orange: 'bg-orange-100 text-orange-600',
  rose: 'bg-rose-100 text-rose-600',
  purple: 'bg-purple-100 text-purple-600',
  indigo: 'bg-indigo-100 text-indigo-600',
  teal: 'bg-teal-100 text-teal-600',
  gray: 'bg-gray-100 text-gray-600',
  sky: 'bg-sky-100 text-sky-600'
};

const RecentActivity = ({ userId, limit = 8 }) => {
  const { isDark } = useTheme();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActivities = async () => {
      if (!userId) return;
      
      try {
        const res = await fetch(`${API_URL}/api/activity/user/${userId}/recent?limit=${limit}`);
        if (res.ok) {
          const data = await res.json();
          setActivities(data.activities || []);
        }
      } catch (error) {
        console.error('Error fetching activities:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [userId, limit]);

  const formatTime = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'Recently';
    }
  };

  if (loading) {
    return (
      <div className={`rounded-2xl p-6 ${isDark ? 'bg-white/5' : 'bg-white border border-gray-200'}`}>
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-200" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-3 bg-gray-200 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className={`rounded-2xl p-6 text-center ${isDark ? 'bg-white/5' : 'bg-white border border-gray-200'}`}>
        <Activity className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-white/30' : 'text-gray-300'}`} />
        <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
          No recent activity yet
        </p>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl overflow-hidden ${isDark ? 'bg-white/5' : 'bg-white border border-gray-200'}`}>
      <div className={`px-4 py-3 border-b flex items-center justify-between ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
        <h3 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
          Recent Activity
        </h3>
        <Badge variant="secondary" className="text-xs">
          {activities.length} events
        </Badge>
      </div>
      
      <div className="divide-y divide-gray-100 dark:divide-white/5 max-h-[400px] overflow-y-auto">
        {activities.map((activity, index) => {
          const IconComponent = ICONS[activity.icon] || Activity;
          const colorClass = COLORS[activity.color] || COLORS.gray;
          
          return (
            <div 
              key={index}
              className={`px-4 py-3 flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors`}
            >
              <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                <IconComponent className="w-4 h-4" />
              </div>
              
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                  {activity.title}
                </p>
                {activity.description && (
                  <p className={`text-xs mt-0.5 truncate ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                    {activity.description}
                  </p>
                )}
                <p className={`text-xs mt-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                  {formatTime(activity.created_at)}
                </p>
              </div>
            </div>
          );
        })}
      </div>
      
      {activities.length >= limit && (
        <div className={`px-4 py-2 border-t text-center ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
          <button className="text-xs text-ux-cyan hover:underline flex items-center justify-center gap-1 mx-auto">
            View all activity <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
};

export default RecentActivity;
